import { type RefObject, useCallback, useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'details',
  'embed',
  'iframe',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((el) => {
    if (el.tabIndex === -1) return false;
    if (getComputedStyle(el).visibility === 'hidden') return false;
    // getComputedStyle reflects only the element's own display value, not an
    // ancestor's. Walk up to the container to catch a display:none parent.
    let node: HTMLElement | null = el;
    while (node && node !== container) {
      if (getComputedStyle(node).display === 'none') return false;
      node = node.parentElement;
    }
    return true;
  });
}

export default function useFocusTrap(
  containerRef: RefObject<HTMLElement>,
  enabled: boolean,
): () => void {
  const savedFocusRef = useRef<HTMLElement | null>(null);
  const enabledRef = useRef(enabled);
  const prevEnabledRef = useRef(false);

  /**
   * Capture the element that held focus immediately before the trap turned on —
   * i.e. the control that opened the dialog. This runs during render, before
   * any child effect fires, so an initially-open child popup (Dropdown/
   * DatePicker) that portals itself out of the dialog and self-focuses can't be
   * mistaken for the opener. Used both to decide whether reclaiming focus is
   * safe and to restore focus when the dialog closes.
   */
  if (prevEnabledRef.current !== enabled) {
    prevEnabledRef.current = enabled;
    if (enabled) {
      const active = document.activeElement;
      savedFocusRef.current = active instanceof HTMLElement ? active : null;
    }
  }

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  /**
   * Moves focus to the dialog container on open. Skips when focus already lives
   * inside the container, and when focus has moved into a popup (Dropdown/
   * DatePicker/Tooltip) this dialog spawned into a sibling portal on
   * document.body — stealing it would trigger that popup's focus-out dismissal
   * and collapse it. Focus is only reclaimed when it still rests on the opener
   * or on nothing (document.body), which is the accessibility case we care
   * about.
   */
  const focusContainer = useCallback(() => {
    if (!enabledRef.current) return;
    const container = containerRef.current;
    if (!container) return;
    const active = document.activeElement;
    if (container.contains(active)) return;
    if (
      active instanceof HTMLElement &&
      active !== document.body &&
      active !== savedFocusRef.current
    ) {
      return;
    }
    container.focus();
  }, [containerRef]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const container = containerRef.current;
      if (e.key !== 'Tab' || !container) return;

      const elements = getFocusableElements(container);
      const first = elements[0];
      const last = elements[elements.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        if (
          active === first ||
          active === container ||
          !container.contains(active)
        ) {
          e.preventDefault();
          (last ?? container).focus();
        }
      } else if (
        active === last ||
        active === container ||
        !container.contains(active)
      ) {
        e.preventDefault();
        (first ?? container).focus();
      }
    },
    [containerRef],
  );

  useEffect(() => {
    if (enabled) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (enabled) {
        const prevFocus = savedFocusRef.current;
        if (prevFocus && typeof prevFocus.focus === 'function') {
          prevFocus.focus();
        }
      }
    };
  }, [containerRef, enabled, handleKeyDown]);

  return focusContainer;
}
